import * as React from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  CheckboxVisibility,
  SelectionMode,
  Icon,
  ISelectableOption,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { LiquidacionInsumo } from '../../../../../system/settlement/aje/liquidacion-insumos';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../../../../system';
import { TipoInsumos } from '../../../carga/enums/tipo_insumos.enum';
import {
  AgrupadorCorporacionLiquidadora,
  AgrupadorCorporacionLiquidadoraTipo,
} from '../../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';
import { t } from 'i18next';
import { getStatusCircleClass } from '../../../carga/utils/settlementUtils';
import {
  validateAjeFilmsProjects,
  validateAsesorias,
  validateCostoHora,
  validateFile,
  validateRegistroActividades,
  validateSubDiario,
  validateInsumoDerivacionDeCostos, 
  validateInsumoGastosBolsita, 
  validateLiquidacionAjeFilms
} from './validaciones-insumos';

const formatValida = (item: LiquidacionInsumo) => {
  if (item.valida === true) {
    return <Icon iconName="CheckMark" className="color-success" />;
  } else {
    return <Icon iconName="Cancel" className="color-error" />;
  }
};

const formatDescription = (item: LiquidacionInsumo) => {
  if (item.loading) {
    return (
      <p className="color-initial" style={{ margin: 0 }}>
        {t('Validando...')}
      </p>
    );
  }
  if (item.valida === true) {
    return (
      <p className="color-initial" style={{ margin: 0 }}>
        {t(item.validacion)}
      </p>
    );
  } else {
    return (
      <p className="color-error" style={{ margin: 0 }}>
        {t(item.validacion)}
      </p>
    );
  }
};

const TIPOS_VALIDAN_POR_ESTADO_VALIDA = [
  TipoInsumos.COSTO_HORA,
  TipoInsumos.REGISTRO_ACTIVIDADES,
  TipoInsumos.LIQUIDACION_AJE_FILMS, 
  TipoInsumos.DISTRIBUCION_MANUAL_GASTOS_BOLSITA, 
  TipoInsumos.PLANTILLA_DERIVACION_COSTOS,
  TipoInsumos.HORAS_ASESORIAS_PERU_RESUMEN,
  TipoInsumos.HORAS_ASESORIAS_PERU_DETALLADO,
  TipoInsumos.HORAS_ASESORIAS_PERU_RESUMEN_XLSX,
  TipoInsumos.HORAS_ASESORIAS_ESPANA_RESUMEN,
  TipoInsumos.HORAS_ASESORIAS_ESPANA_DETALLADO,
  TipoInsumos.HORAS_ASESORIAS_ESPANA_RESUMEN_XLSX,
  TipoInsumos.HORAS_ASESORIAS_AJEFILMS_DETALLADO,
  TipoInsumos.HORAS_ASESORIAS_AJEFILMS_RESUMEN,
  TipoInsumos.HORAS_ASESORIAS_AJEFILMS_RESUMEN_XLSX,
  TipoInsumos.HORAS_PROYECTOS_AJEFILMS_DETALLADO,
  TipoInsumos.HORAS_PROYECTOS_AJEFILMS_RESUMEN,
  TipoInsumos.HORAS_PROYECTOS_AJEFILMS_RESUMEN_XLSX,
];

const updateItemByIndex = (prev: LiquidacionInsumo[], index: number, patch: Partial<LiquidacionInsumo>) => {
  const next = [...prev];
  next[index] = { ...next[index], ...patch };
  return next;
};

const setItemLoading = (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => {
  setItems((prev) => updateItemByIndex(prev, index, { loading }));
};

interface LevelsTableProps {
  items: LiquidacionInsumo[];
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  periodo: DateTime;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
  system: TimeTaxSupportSystemClient;
  changeBlockUI: (state: boolean) => void;
  setButtonDisability: (can: boolean) => void;
}

export const LevelsTable: React.FC<LevelsTableProps> = ({ items, periodo, system, settlementType, setItems, changeBlockUI, setButtonDisability }) => {
  const { t } = useTranslation();

  const validateInsumos = async ({
    items,
    periodo,
    system,
    setItems,
    changeBlockUI,
  }: {
    items: LiquidacionInsumo[];
    periodo: DateTime;
    system: TimeTaxSupportSystemClient;
    setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
    changeBlockUI: (state: boolean) => void;
  }) => {
    const registroActividadesIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.REGISTRO_ACTIVIDADES);
    const costoHoraIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.COSTO_HORA);
    const subDiarioIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.SUB_DIARIO);

    const ccIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.CC);
    const pygIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.PYG);
    const ventasIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.VENTAS);
    // const horasHnosIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_HNOS);
    // const horasPeruIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_PERU);
    const pygIfrsEsIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.PYG_IFRS_ES);
    // const horasEspanaIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ESPANA);
    // const horasAjefilmsIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_AJEFILMS);
    const subDiarioEspanaIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.SUBDIARIO_ESPANA);
    const otrosGastosHorasIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.OTROS_GASTOS_HORAS);
    const otrosGastosFacturacionIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.OTROS_GASTOS_FACTURACION);
    const distribucionManualGastosBolsitaIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.DISTRIBUCION_MANUAL_GASTOS_BOLSITA);
    const plantillaDerivacionCostosIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.PLANTILLA_DERIVACION_COSTOS);

    const liquidacionAjeFilmsIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.LIQUIDACION_AJE_FILMS);
    const horasAsesoriasPeruResumenIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_PERU_RESUMEN);
    const horasAsesoriasPeruDetalladoIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_PERU_DETALLADO);
    const horasAsesoriasPeruResumenXlsxIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_PERU_RESUMEN_XLSX);
    const horasAsesoriasEspanaResumenIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_ESPANA_RESUMEN);
    const horasAsesoriasEspanaDetalladoIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_ESPANA_DETALLADO);
    const horasAsesoriasEspanaResumenXlsxIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_ESPANA_RESUMEN_XLSX);
    const horasAsesoriasAjefilmsDetalladoIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_AJEFILMS_DETALLADO);
    const horasAsesoriasAjefilmsResumenIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_AJEFILMS_RESUMEN);
    const horasAsesoriasAjefilmsResumenXlsxIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_ASESORIAS_AJEFILMS_RESUMEN_XLSX);
    const horasProyectosAjefilmsDetalladoIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_PROYECTOS_AJEFILMS_DETALLADO);
    const horasProyectosAjefilmsResumenIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_PROYECTOS_AJEFILMS_RESUMEN);
    const horasProyectosAjefilmsResumenXlsxIndex = items.findIndex((i) => i.inst_codigo === TipoInsumos.HORAS_PROYECTOS_AJEFILMS_RESUMEN_XLSX);

    // SI EL TIPO DE AGRUPADOR ES AJE_FILMS SOLO SE VALIDAN REGISTRO DE ACTIVIDADES, COSTO HORA Y SUB DIARIO
    if (settlementType.data && settlementType.data.agrcorliq_tipo === AgrupadorCorporacionLiquidadoraTipo.AJE_FILMS) {
      // REGISTRO DE ACTIVIDADES
      if (registroActividadesIndex !== -1 && settlementType.data) {
        await validateRegistroActividades({
          index: registroActividadesIndex,
          periodo,
          system,
          settlementType,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // COSTO HORA
      if (costoHoraIndex !== -1) {
        await validateCostoHora({
          index: costoHoraIndex,
          periodo,
          system,
          settlementType,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // SUB DIARIO
      if (subDiarioIndex !== -1) {
        validateFile({
          index: subDiarioIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      const horasProyectosAjefilms = [
        horasProyectosAjefilmsDetalladoIndex,
        horasProyectosAjefilmsResumenIndex,
        horasProyectosAjefilmsResumenXlsxIndex,
      ].filter((i) => i !== -1);

      if (horasProyectosAjefilms.length > 0) {
        await validateAjeFilmsProjects({
          indices: {
            detallado: horasProyectosAjefilmsDetalladoIndex,
            resumen: horasProyectosAjefilmsResumenIndex,
            resumenXlsx: horasProyectosAjefilmsResumenXlsxIndex,
          },
          periodo,
          system,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }
    }

    // SI EL TIPO DE AGRUPADOR ES ASESORIAS SE VALIDAN TODOS LOS INSUMOS EXCEPTO REGISTRO DE ACTIVIDADES Y COSTO HORA
    else if (settlementType.data && settlementType.data.agrcorliq_tipo === AgrupadorCorporacionLiquidadoraTipo.ASESORIAS) {
      // SUB DIARIO
      if (subDiarioIndex !== -1) {
        validateFile({
          index: subDiarioIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // CC
      if (ccIndex !== -1) {
        validateFile({
          index: ccIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // PYG
      if (pygIndex !== -1) {
        validateFile({
          index: pygIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // VENTAS
      if (ventasIndex !== -1) {
        validateFile({
          index: ventasIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // HORAS HNOS
      /* if (horasHnosIndex !== -1) {
        validateFile({
          index: horasHnosIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      } */

      // HORAS PERU
      /* if (horasPeruIndex !== -1) {
        validateFile({
          index: horasPeruIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      } */

      // PYG IFRS ES
      if (pygIfrsEsIndex !== -1) {
        validateFile({
          index: pygIfrsEsIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // HORAS ESPAÑA
      /* if (horasEspanaIndex !== -1) {
        validateFile({
          index: horasEspanaIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      } */

      // HORAS AJEFILMS
      /* if (horasAjefilmsIndex !== -1) {
        validateFile({
          index: horasAjefilmsIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      } */

      // SUBDIARIO ESPAÑA
      if (subDiarioEspanaIndex !== -1) {
        validateFile({
          index: subDiarioEspanaIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // OTROS GASTOS HORAS
      if (otrosGastosHorasIndex !== -1) {
        validateFile({
          index: otrosGastosHorasIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // OTROS GASTOS FACTURACION
      if (otrosGastosFacturacionIndex !== -1) {
        validateFile({
          index: otrosGastosFacturacionIndex,
          items,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // DISTRIBUCION MANUAL GASTOS BOLSITA
      if (distribucionManualGastosBolsitaIndex !== -1) {
        validateInsumoGastosBolsita({
          index: distribucionManualGastosBolsitaIndex,
          system,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // PLANTILLA DERIVACION COSTOS
      if (plantillaDerivacionCostosIndex !== -1) {
        validateInsumoDerivacionDeCostos({
          index: plantillaDerivacionCostosIndex,
          system,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // LIQUIDACION AJE FILMS
      if (liquidacionAjeFilmsIndex !== -1 && settlementType.data) {
        await validateLiquidacionAjeFilms({
          index: liquidacionAjeFilmsIndex,
          periodo,
          system,
          settlementType,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }

      // HORAS ASESORIAS
      const asesoriasPeru = [horasAsesoriasPeruResumenIndex, horasAsesoriasPeruDetalladoIndex, horasAsesoriasPeruResumenXlsxIndex].filter(
        (i) => i !== -1,
      );
      const asesoriasEspana = [horasAsesoriasEspanaResumenIndex, horasAsesoriasEspanaDetalladoIndex, horasAsesoriasEspanaResumenXlsxIndex].filter(
        (i) => i !== -1,
      );
      const asesoriasAjefilms = [
        horasAsesoriasAjefilmsDetalladoIndex,
        horasAsesoriasAjefilmsResumenIndex,
        horasAsesoriasAjefilmsResumenXlsxIndex,
      ].filter((i) => i !== -1);
      if (asesoriasPeru.length > 0 || asesoriasEspana.length > 0 || asesoriasAjefilms.length > 0) {
        await validateAsesorias({
          peru: {
            detallado: horasAsesoriasPeruDetalladoIndex,
            resumen: horasAsesoriasPeruResumenIndex,
            resumenXlsx: horasAsesoriasPeruResumenXlsxIndex,
          },
          espana: {
            detallado: horasAsesoriasEspanaDetalladoIndex,
            resumen: horasAsesoriasEspanaResumenIndex,
            resumenXlsx: horasAsesoriasEspanaResumenXlsxIndex,
          },
          ajefilms: {
            detallado: horasAsesoriasAjefilmsDetalladoIndex,
            resumen: horasAsesoriasAjefilmsResumenIndex,
            resumenXlsx: horasAsesoriasAjefilmsResumenXlsxIndex,
          },
          periodo,
          system,
          setItems,
          setItemLoading,
          updateItemByIndex,
        });
      }
    } else {
      console.error('Tipo de agrupador liquidador no reconocido para validación de insumos');
    }
  };

  React.useEffect(() => {
    validateInsumos({
      items,
      periodo,
      system,
      setItems,
      changeBlockUI,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, settlementType]);

  React.useEffect(() => {
    const hasLoading = items.some((i) => i.loading);
    const allValid = items.every((i) => i.valida);

    setButtonDisability(allValid && !hasLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const columns = [
    {
      key: 'liqins_id',
      fieldName: 'liqins_id',
      name: t('Estado'),
      minWidth: 50,
      maxWidth: 50,
      isResizable: false,
      onRender(item: LiquidacionInsumo) {
        return (
          <div
            style={{
              width: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              className={`status-films__circle ${getStatusCircleClass(TIPOS_VALIDAN_POR_ESTADO_VALIDA.includes(item.inst_codigo) ? item.valida : !!item.liqins_id)}`}
            />
          </div>
        );
      },
    },
    {
      key: 'inst_nombre',
      fieldName: 'inst_nombre',
      name: t('Insumo'),
      minWidth: 200,
    },
    {
      key: 'valida',
      fieldName: 'valida',
      name: t('Valida'),
      minWidth: 40,
      maxWidth: 40,
      onRender: (item: LiquidacionInsumo) => {
        if (item.loading) {
          return <Spinner className="m-auto" size={SpinnerSize.xSmall} />;
        }
        return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{formatValida(item)}</div>;
      },
    },
    {
      key: 'validacion',
      fieldName: 'validacion',
      name: t('Descripción de la validacion'),
      minWidth: 400,
      maxWidth: 500,
      isResizable: true,
      onRender: (item: LiquidacionInsumo) => formatDescription(item),
    },
  ];

  return (
    <DetailsList
      className="mb-2"
      items={items}
      columns={columns}
      selectionMode={SelectionMode.none}
      checkboxVisibility={CheckboxVisibility.hidden}
      layoutMode={DetailsListLayoutMode.fixedColumns}
    />
  );
};
